import { theme } from 'lib/theme';
import * as React from 'react';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { ViewWithSeparators } from './ViewWithSeparators';
import {
  BUTTON_COMPACT_HEIGHT,
  BUTTON_HEIGHT,
  Button,
  ButtonTheme,
} from './Button';
import { TEXT_SIZE_NORMAL } from 'components/Txt';

type Props<K extends string> = {
  /**
   * Return false to not call setActiveKey internally
   */
  onActiveChange: (activeItem: K) => boolean | void;
  buttons: { text: string; key: K; rbs?: boolean; disabled?: boolean }[];
  active: string;
  size?: 'small';
  rounded?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  buttonStyle?: StyleProp<ViewStyle>;
};

const TOGGLE_HEIGHT = BUTTON_HEIGHT + 8;
const TOGGLE_COMPACT_HEIGHT = BUTTON_COMPACT_HEIGHT + 6;

export const ButtonToggleGroup = <K extends string>({
  buttons,
  active,
  onActiveChange,
  size,
  rounded,
  containerStyle,
  buttonStyle,
}: Props<K>): React.ReactElement => {
  const [activeKey, setActiveKey] = React.useState(active);
  const lastIncomingActive = React.useRef(active);

  const handleOnPress = React.useCallback(
    (newActiveKey: K) => {
      const afterChange = onActiveChange(newActiveKey);
      if (afterChange !== false) {
        setActiveKey(newActiveKey);
      }
    },
    [onActiveChange],
  );

  const ButtonsComponent = React.useMemo(
    () =>
      buttons.map((button) => {
        const isActive = button.key === activeKey;
        return (
          <Button
            key={button.key}
            text={button.text}
            chipLike={rounded}
            disabled={button.disabled}
            buttonTheme={isActive ? ButtonTheme.primary : ButtonTheme.themeless}
            onPress={isActive ? undefined : () => handleOnPress(button.key)}
            style={[
              size === 'small' ? styles.buttonSmall : styles.button,
              isActive && styles.buttonActive,
              buttonStyle,
            ]}
            textStyle={[
              { fontSize: TEXT_SIZE_NORMAL },
              { color: theme.colors.text },
            ]}
            styleHovered={{ backgroundColor: 'transparent' }}
            compact
            rbs={button.rbs}
          />
        );
      }),
    [buttons, activeKey, size, buttonStyle, rounded, handleOnPress],
  );

  React.useEffect(() => {
    if (lastIncomingActive.current !== active) {
      setActiveKey(active);
      lastIncomingActive.current = active;
    }
  }, [active]);

  return (
    <ViewWithSeparators
      style={[
        styles.container,
        rounded && { borderRadius: 999 },
        containerStyle,
      ]}
      separator={<View />}
    >
      {ButtonsComponent}
    </ViewWithSeparators>
  );
};

const styles = StyleSheet.create({
  button: {
    borderWidth: 0,
    height: TOGGLE_HEIGHT,
    justifyContent: 'center',
    flex: 1,
  },
  buttonSmall: {
    borderWidth: 0,
    height: TOGGLE_COMPACT_HEIGHT,
    justifyContent: 'center',
    flex: 1,
  },
  buttonActive: {
    opacity: 1,
    backgroundColor: theme.colors.white,
    borderWidth: 0,
  },
  container: {
    // give the bar a subtle tint so the white active pill looks inset
    backgroundColor: theme.colors.black10,
    padding: 2,
    borderRadius: 4,
    flexDirection: 'row',
  },
});
